import { useEffect, useMemo, useState } from 'react';
import { FileText } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { PageHeader } from '../../components/ui/PageHeader/PageHeader';
import { Card } from '../../components/ui/Card/Card';
import { Button } from '../../components/ui/Button/Button';
import { Input } from '../../components/form/Input';
import { Select } from '../../components/form/Select';
import { DatePicker } from '../../components/form/DatePicker';
import { Checkbox } from '../../components/form/Checkbox';
import { SignaturePad } from '../../components/ui/SignaturePad/SignaturePad';
import { BookingLeftPanel } from '../../components/ui/BookingLeftPanel/BookingLeftPanel';
import { ConfirmModal } from '../../components/ui/ConfirmModal/ConfirmModal';
import {
  ImportFromEmailModal,
  type ParsedBookingFields,
} from '../../components/ui/ImportFromEmailModal/ImportFromEmailModal';
import { DraftManagerModal } from '../../components/drafts/DraftManagerModal';
import { useToast } from '../../hooks/useToast';
import { useDraftManager } from '../../hooks/useDraftManager';
import {
  useCheckBankDetails,
  useCreateReceipt,
  useUploadReceiptPhotos,
} from './hooks/useReceiptMutations';
import { BankDetailsUpdateModal } from './components/BankDetailsUpdateModal';
import { usePaymentModes, useProducts, useWorkSources } from '../bookings/hooks/useReferenceData';
import { getPaymentKind } from './paymentMode';
import { PhotoSlotsInput } from './components/PhotoSlotsInput';
import { slotFilesToUpload, type SlotFiles } from './photoSlots';
import type {
  CreateReceiptInput,
  CustomerSummary,
  ReceiptDetailResponse,
  ReceiptDuplicateWarning,
} from '../../types/api';
import styles from './ReceiptDirectCreatePage.module.css';

interface FormState {
  receipt_date: string;
  car_registration: string;
  car_make_model: string;
  car_color: string;
  car_year: string;
  car_mileage: string;
  chassis: string;
  has_mot: boolean;
  has_keys: boolean;
  has_logbook: boolean;
  customer_name: string;
  customer_email: string;
  customer_phone: string;
  customer_address: string;
  customer_postcode: string;
  amount: string;
  payment_mode_uuid: string;
  product_uuid: string;
  source_uuid: string;
  declaration_accepted: boolean;
  cheque_number: string;
  account_name: string;
  account_number: string;
  sort_code: string;
  notes: string;
}

const today = new Date().toISOString().slice(0, 10);

const INITIAL: FormState = {
  receipt_date: today,
  car_registration: '',
  car_make_model: '',
  car_color: '',
  car_year: '',
  car_mileage: '',
  chassis: '',
  has_mot: false,
  has_keys: false,
  has_logbook: false,
  customer_name: '',
  customer_email: '',
  customer_phone: '',
  customer_address: '',
  customer_postcode: '',
  amount: '',
  payment_mode_uuid: '',
  product_uuid: '',
  source_uuid: '',
  declaration_accepted: false,
  cheque_number: '',
  account_name: '',
  account_number: '',
  sort_code: '',
  notes: '',
};

// The serialisable slice of the receipt form persisted as a draft. Uploaded
// photos are File objects and cannot be round-tripped, so they are excluded;
// signatures (base64) and the linked customer are kept.
interface ReceiptDraftData {
  form: FormState;
  sellerSig: string | null;
  buyerSig: string | null;
  selectedCustomer: CustomerSummary | null;
}

function receiptDraftIsPopulated(d: ReceiptDraftData): boolean {
  const f = d.form;
  return Boolean(
    f.car_registration.trim() ||
      f.car_make_model.trim() ||
      f.customer_name.trim() ||
      f.amount.trim(),
  );
}

function sanitiseFormState(f: FormState): FormState {
  const result = { ...f };
  for (const key of Object.keys(result) as Array<keyof FormState>) {
    const val = result[key];
    if (typeof INITIAL[key] === 'string' && (val === null || val === undefined)) {
      (result as Record<string, unknown>)[key] = '';
    }
  }
  return result;
}

function deserialiseReceiptDraft(raw: unknown): ReceiptDraftData | null {
  if (!raw || typeof raw !== 'object') return null;
  const obj = raw as Record<string, unknown>;
  // New shape: { form, sellerSig, buyerSig, selectedCustomer }.
  if (obj.form && typeof obj.form === 'object') {
    return {
      form: sanitiseFormState({ ...INITIAL, ...(obj.form as Partial<FormState>) }),
      sellerSig: typeof obj.sellerSig === 'string' ? obj.sellerSig : null,
      buyerSig: typeof obj.buyerSig === 'string' ? obj.buyerSig : null,
      selectedCustomer: (obj.selectedCustomer as CustomerSummary | null) ?? null,
    };
  }
  // Legacy / flat shape: the bare FormState.
  if ('amount' in obj || 'car_registration' in obj) {
    return {
      form: sanitiseFormState({ ...INITIAL, ...(obj as Partial<FormState>) }),
      sellerSig: null,
      buyerSig: null,
      selectedCustomer: null,
    };
  }
  return null;
}

function receiptDraftTitle(d: ReceiptDraftData): string {
  const f = d.form;
  const amount = f.amount.trim();
  const reg = f.car_registration.trim();
  if (amount && reg) return `Receipt — £${amount} (${reg})`;
  if (amount) return `Receipt — £${amount}`;
  if (reg) return `Receipt — ${reg}`;
  return 'Receipt draft';
}

export function ReceiptDirectCreatePage(): JSX.Element {
  const navigate = useNavigate();
  const toast = useToast();
  const [form, setForm] = useState<FormState>(INITIAL);
  const [photoSlots, setPhotoSlots] = useState<SlotFiles>({});
  const [sellerSig, setSellerSig] = useState<string | null>(null);
  const [buyerSig, setBuyerSig] = useState<string | null>(null);
  const [selectedCustomer, setSelectedCustomer] = useState<CustomerSummary | null>(null);
  const [importOpen, setImportOpen] = useState(false);
  const [draftsOpen, setDraftsOpen] = useState(false);
  // Set after a successful create that came back with a duplicate_warning:
  // the form stays up so the notice can be read before moving on.
  const [created, setCreated] = useState<{
    encId: string;
    reference: string | null;
    warning: ReceiptDuplicateWarning;
  } | null>(null);

  const createMutation = useCreateReceipt();
  const checkBank = useCheckBankDetails();
  const [bankPrompt, setBankPrompt] = useState<{
    input: CreateReceiptInput;
    fields: string[];
  } | null>(null);
  const uploadPhotos = useUploadReceiptPhotos();
  const paymentModes = usePaymentModes();
  const products = useProducts();
  const workSources = useWorkSources();

  // Draft persistence — mirrors the booking flow (auto-save every 30s,
  // auto-resume on mount, manual save/load/delete via the Draft Manager).
  const draftData = useMemo<ReceiptDraftData>(
    () => ({ form, sellerSig, buyerSig, selectedCustomer }),
    [form, sellerSig, buyerSig, selectedCustomer],
  );
  const draftManager = useDraftManager<ReceiptDraftData>({
    draftType: 'receipt',
    formData: draftData,
    onLoad: (data) => {
      setForm(data.form);
      setSellerSig(data.sellerSig);
      setBuyerSig(data.buyerSig);
      setSelectedCustomer(data.selectedCustomer);
    },
    isPopulated: receiptDraftIsPopulated,
    deserialize: deserialiseReceiptDraft,
    getTitle: receiptDraftTitle,
  });

  function setField<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  function applyCustomer(c: CustomerSummary) {
    setSelectedCustomer(c);
    setForm((prev) => ({
      ...prev,
      customer_name: c.full_name,
      customer_email: c.email ?? '',
      customer_phone: c.phone,
      customer_address: c.address,
      customer_postcode: c.postcode,
      // Prefill bank details from customer record when blank — legacy parity.
      account_name: prev.account_name || (c.account_name ?? ''),
      account_number: prev.account_number || (c.account_number ?? ''),
      sort_code: prev.sort_code || (c.sort_code ?? ''),
    }));
  }

  // Map parsed-email payload onto receipt FormState. Canonical fields
  // (name / car_make_model / car_registration) are preferred; legacy
  // aliases (customer_name / vehicle_make_model / vehicle_reg) are the
  // fallback so older parser outputs still populate the form. Only
  // truthy values are written — a null/empty parse result never blanks
  // out a field the user has already typed.
  function applyEmailImport(parsed: ParsedBookingFields) {
    setForm((prev) => {
      const next = { ...prev };
      const name = parsed.name ?? parsed.customer_name;
      if (name) next.customer_name = name;
      if (parsed.phone) next.customer_phone = parsed.phone;
      if (parsed.email) next.customer_email = parsed.email;
      if (parsed.address) next.customer_address = parsed.address;
      if (parsed.postcode) next.customer_postcode = parsed.postcode.toUpperCase();
      const reg = parsed.car_registration ?? parsed.vehicle_reg;
      if (reg) next.car_registration = reg.toUpperCase();
      const makeModel = parsed.car_make_model ?? parsed.vehicle_make_model;
      if (makeModel) next.car_make_model = makeModel;
      if (parsed.price) next.amount = parsed.price;
      if (parsed.notes) next.notes = parsed.notes;
      return next;
    });
  }

  const productOptions = useMemo(
    () => (products.data ?? []).map((p) => ({ value: p.uuid, label: p.name })),
    [products.data],
  );
  const sourceOptions = useMemo(
    () => (workSources.data ?? []).map((s) => ({ value: s.uuid, label: s.name })),
    [workSources.data],
  );

  const paymentOptions = useMemo(
    () =>
      (paymentModes.data ?? []).map((p) => ({
        value: p.uuid,
        label: p.name,
      })),
    [paymentModes.data],
  );

  // Conditional rendering for the cheque vs. bank-details inputs mirrors
  // the legacy edit_receipt.php behaviour: cheque-named modes show only
  // the cheque field, bank/transfer modes show only the bank trio, and
  // any other mode (Cash / Card / Free Collect / Bread4Scrap) hides
  // both blocks.
  const selectedPaymentMode = useMemo(
    () =>
      (paymentModes.data ?? []).find((p) => p.uuid === form.payment_mode_uuid) ??
      null,
    [paymentModes.data, form.payment_mode_uuid],
  );
  const paymentKind = getPaymentKind(selectedPaymentMode?.name ?? null);

  // Default to "Cash" (or the first available) once payment modes load,
  // so the form has a consistent starting state — matches legacy parity
  // where a payment_mode is always set on submit.
  useEffect(() => {
    if (form.payment_mode_uuid !== '') return;
    const list = paymentModes.data ?? [];
    const first = list.find((p) => p.name.toLowerCase() === 'cash') ?? list[0];
    if (!first) return;
    setField('payment_mode_uuid', first.uuid);
    // We only want this to run once the list arrives; payment_mode_uuid
    // is read from the latest state via the early return above.
  }, [paymentModes.data]);

  const errors = useMemo(() => {
    const e: string[] = [];
    if (!form.car_registration.trim()) e.push('Vehicle registration is required.');
    if (!form.car_make_model.trim()) e.push('Make / model is required.');
    if (!form.customer_name.trim()) e.push('Customer name is required.');
    if (!form.customer_phone.trim()) e.push('Customer phone is required.');
    if (!form.customer_address.trim()) e.push('Customer address is required.');
    if (!form.customer_postcode.trim()) e.push('Customer postcode is required.');
    if (!form.amount || Number.isNaN(Number(form.amount))) e.push('Amount is required.');
    if (!form.product_uuid) e.push('Product is required.');
    if (!form.source_uuid) e.push('Source is required.');
    if (!form.declaration_accepted) e.push('The declaration must be accepted.');
    if (paymentKind === 'cheque' && !form.cheque_number.trim()) {
      e.push('Cheque number is required for cheque payments.');
    }
    return e;
  }, [form, paymentKind]);

  const canSubmit = errors.length === 0 && !createMutation.isPending && !uploadPhotos.isPending;

  async function handleSubmit() {
    if (!canSubmit) return;
    const input = buildInput();

    // Batch 7b: a linked customer whose stored bank details differ from this
    // receipt's → ask before overwriting them.
    const hasBank =
      input.account_name != null || input.account_number != null || input.sort_code != null;
    if (selectedCustomer !== null && hasBank) {
      try {
        const check = await checkBank.mutateAsync({
          customer_uuid: selectedCustomer.uuid,
          account_name: input.account_name ?? null,
          account_number: input.account_number ?? null,
          sort_code: input.sort_code ?? null,
        });
        if (check.differs) {
          setBankPrompt({ input, fields: check.fields });
          return;
        }
      } catch {
        // Check failed: save without overwriting (first capture still applies).
      }
    }
    await submitReceipt(input);
  }

  function buildInput(): CreateReceiptInput {
    const input: CreateReceiptInput = {
      // Link the chosen customer record (previously only the snapshot fields
      // were sent, so a selected customer was never attached to the receipt).
      customer_uuid: selectedCustomer?.uuid,
      receipt_date: form.receipt_date,
      car_registration: form.car_registration.trim().toUpperCase(),
      car_make_model: form.car_make_model.trim(),
      car_color: form.car_color.trim() || undefined,
      car_year: form.car_year ? Number(form.car_year) : undefined,
      car_mileage: form.car_mileage ? Number(form.car_mileage) : undefined,
      chassis: form.chassis.trim() || undefined,
      has_mot: form.has_mot,
      has_keys: form.has_keys,
      has_logbook: form.has_logbook,
      customer_name: form.customer_name.trim(),
      customer_email: form.customer_email.trim() || null,
      customer_phone: form.customer_phone.trim(),
      customer_address: form.customer_address.trim(),
      customer_postcode: form.customer_postcode.trim().toUpperCase(),
      amount: Number(form.amount),
      payment_mode_uuid: form.payment_mode_uuid || undefined,
      product_uuid: form.product_uuid,
      source_uuid: form.source_uuid,
      // Payment-detail fields — only forwarded when the selected mode is
      // the matching kind. Switching mode after typing in a field drops
      // the stale value rather than persisting it, matching the
      // conditional UI. Blank fields are sent as `undefined` (not `null`)
      // so the body validates against pre-`.nullable()` builds; the
      // columns are nullable in the DB so omitted rows still land as
      // NULL.
      cheque_number:
        paymentKind === 'cheque' && form.cheque_number.trim().length > 0
          ? form.cheque_number.trim()
          : undefined,
      account_name:
        paymentKind === 'bank' && form.account_name.trim().length > 0
          ? form.account_name.trim()
          : undefined,
      account_number:
        paymentKind === 'bank' && form.account_number.trim().length > 0
          ? form.account_number.trim()
          : undefined,
      sort_code:
        paymentKind === 'bank' && form.sort_code.trim().length > 0
          ? form.sort_code.trim()
          : undefined,
      notes: form.notes.trim() || null,
      declaration_accepted: form.declaration_accepted,
    };

    if (sellerSig) input.seller_signature = sellerSig;
    if (buyerSig) input.buyer_signature = buyerSig;
    return input;
  }

  async function submitReceipt(input: CreateReceiptInput) {
    try {
      const res = await createMutation.mutateAsync(input);
      const body = res.data as ReceiptDetailResponse;
      const detail = body.receipt;
      const encId = detail.enc_id;
      const warning = body.duplicate_warning ?? null;

      const { files, captions } = slotFilesToUpload(photoSlots);
      if (files.length > 0) {
        try {
          await uploadPhotos.mutateAsync({ encId, files, captions });
        } catch {
          toast.warning('Receipt created, but photos failed to upload. Try uploading from the detail page.');
        }
      }

      // Drop the draft so a refresh doesn't re-offer the just-saved receipt.
      await draftManager.clearDraft();

      toast.success('Receipt created');
      if (warning !== null) {
        // Non-blocking: the receipt exists. Stay put so the notice is seen.
        setCreated({ encId, reference: detail.reference, warning });
        return;
      }
      navigate(`/collections/receipts/${encodeURIComponent(encId)}`);
    } catch {
      // error toast handled in mutation
    }
  }

  return (
    <div className={styles.page}>
      <PageHeader
        title="New Receipt"
        actions={
          <>
            <Button
              variant="secondary"
              icon={FileText}
              onClick={() => setDraftsOpen(true)}
              disabled={createMutation.isPending}
            >
              Drafts
            </Button>
            <Button
              variant="secondary"
              onClick={() => navigate('/collections/receipts')}
              disabled={createMutation.isPending}
            >
              Cancel
            </Button>
          </>
        }
      />

      {created !== null ? (
        <div role="status" className={styles.duplicateNotice} data-testid="duplicate-warning">
          <p>
            Receipt <strong>{created.reference ?? 'created'}</strong> was created, but this
            registration already has a receipt in the last {created.warning.window_days} days:{' '}
            <Link
              to={`/collections/receipts/${encodeURIComponent(created.warning.existing_receipt_id)}`}
            >
              {created.warning.existing_reference ?? 'View existing receipt'}
            </Link>{' '}
            ({created.warning.existing_receipt_date}).
          </p>
          <Button
            variant="primary"
            onClick={() => navigate(`/collections/receipts/${encodeURIComponent(created.encId)}`)}
          >
            Open the new receipt
          </Button>
        </div>
      ) : null}

      <div className={styles.layout}>
      <BookingLeftPanel
        selectedCustomer={selectedCustomer}
        onCustomerSelect={applyCustomer}
        onImportFromEmail={() => setImportOpen(true)}
      />
      <div className={styles.main}>

      <Card title="Receipt details">
        <div className={styles.grid2}>
          <DatePicker
            label="Receipt date"
            value={form.receipt_date}
            onChange={(e) => setField('receipt_date', e.target.value)}
          />
          <Input
            label="Amount (£)"
            variant="number"
            required
            value={form.amount}
            onChange={(e) => setField('amount', e.target.value)}
          />
        </div>
      </Card>

      <Card title="Product and source">
        <div className={styles.grid2}>
          <Select
            label="Product"
            required
            options={productOptions}
            placeholder={products.isLoading ? 'Loading…' : 'Select a product'}
            value={form.product_uuid}
            onChange={(e) => setField('product_uuid', e.target.value)}
          />
          <Select
            label="Source"
            required
            options={sourceOptions}
            placeholder={workSources.isLoading ? 'Loading…' : 'Select a source'}
            value={form.source_uuid}
            onChange={(e) => setField('source_uuid', e.target.value)}
          />
        </div>
      </Card>

      <Card title="Vehicle">
        <div className={styles.grid2}>
          <Input
            label="Registration"
            required
            value={form.car_registration}
            onChange={(e) => setField('car_registration', e.target.value.toUpperCase())}
          />
          <Input
            label="Make / Model"
            required
            value={form.car_make_model}
            onChange={(e) => setField('car_make_model', e.target.value)}
          />
          <Input
            label="Colour"
            value={form.car_color}
            onChange={(e) => setField('car_color', e.target.value)}
          />
          <Input
            label="Year"
            variant="number"
            value={form.car_year}
            onChange={(e) => setField('car_year', e.target.value)}
          />
          <Input
            label="Mileage"
            variant="number"
            value={form.car_mileage}
            onChange={(e) => setField('car_mileage', e.target.value)}
          />
          <Input
            label="Chassis"
            value={form.chassis}
            onChange={(e) => setField('chassis', e.target.value)}
          />
        </div>
        <div className={styles.checkboxRow} style={{ marginTop: '0.75rem' }}>
          <Checkbox
            label="MOT present"
            checked={form.has_mot}
            onChange={(e) => setField('has_mot', e.target.checked)}
          />
          <Checkbox
            label="Keys present"
            checked={form.has_keys}
            onChange={(e) => setField('has_keys', e.target.checked)}
          />
          <Checkbox
            label="Logbook present"
            checked={form.has_logbook}
            onChange={(e) => setField('has_logbook', e.target.checked)}
          />
        </div>
      </Card>

      <Card title="Customer">
        <div className={styles.grid2}>
          <Input
            label="Name"
            required
            value={form.customer_name}
            onChange={(e) => setField('customer_name', e.target.value)}
          />
          <Input
            label="Phone"
            variant="tel"
            required
            value={form.customer_phone}
            onChange={(e) => setField('customer_phone', e.target.value)}
          />
          <Input
            label="Email"
            variant="email"
            value={form.customer_email}
            onChange={(e) => setField('customer_email', e.target.value)}
          />
          <Input
            label="Address"
            required
            value={form.customer_address}
            onChange={(e) => setField('customer_address', e.target.value)}
          />
          <Input
            label="Postcode"
            required
            value={form.customer_postcode}
            onChange={(e) => setField('customer_postcode', e.target.value.toUpperCase())}
          />
        </div>
      </Card>

      <Card title="Payment">
        <div className={styles.grid2}>
          <Select
            label="Payment method"
            options={paymentOptions}
            placeholder={
              paymentModes.isLoading
                ? 'Loading…'
                : paymentOptions.length === 0
                  ? 'No options — leave blank'
                  : '—'
            }
            value={form.payment_mode_uuid}
            onChange={(e) => setField('payment_mode_uuid', e.target.value)}
          />
          {paymentKind === 'cheque' ? (
            <Input
              label="Cheque Number"
              value={form.cheque_number}
              onChange={(e) => setField('cheque_number', e.target.value)}
            />
          ) : null}
          {paymentKind === 'bank' ? (
            <>
              <Input
                label="Account Name"
                value={form.account_name}
                onChange={(e) => setField('account_name', e.target.value)}
              />
              <Input
                label="Account Number"
                value={form.account_number}
                onChange={(e) => setField('account_number', e.target.value)}
              />
              <Input
                label="Sort Code"
                value={form.sort_code}
                onChange={(e) => setField('sort_code', e.target.value)}
              />
            </>
          ) : null}
        </div>
      </Card>

      <Card title="Photos">
        <PhotoSlotsInput value={photoSlots} onChange={setPhotoSlots} />
      </Card>

      <Card title="Signatures">
        <div className={styles.signatureGrid}>
          <SignaturePad label="Seller signature" value={sellerSig} onChange={setSellerSig} />
          <SignaturePad label="Buyer signature" value={buyerSig} onChange={setBuyerSig} />
        </div>
      </Card>

      <Card title="Declaration">
        <Checkbox
          label="The seller declares they are the lawful owner of the vehicle and entitled to sell it."
          checked={form.declaration_accepted}
          onChange={(e) => setField('declaration_accepted', e.target.checked)}
        />
      </Card>

      <Card title="Notes">
        <textarea
          rows={3}
          style={{ width: '100%', resize: 'vertical' }}
          value={form.notes}
          onChange={(e) => setField('notes', e.target.value)}
          aria-label="Notes"
        />
      </Card>

      <div className={styles.submitBar}>
        <ul className={styles.errorList} aria-live="polite">
          {errors.slice(0, 4).map((msg) => (
            <li key={msg}>{msg}</li>
          ))}
        </ul>
        <Button
          variant="primary"
          onClick={() => void handleSubmit()}
          loading={createMutation.isPending || uploadPhotos.isPending}
          disabled={!canSubmit}
        >
          Create Receipt
        </Button>
      </div>

      <ImportFromEmailModal
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onApply={applyEmailImport}
      />

      {/* Resume-draft prompt */}
      <ConfirmModal
        open={draftManager.pendingDraftPrompt !== null}
        onCancel={draftManager.rejectDraft}
        onConfirm={draftManager.acceptDraft}
        title="Resume saved draft?"
        confirmLabel="Resume draft"
        cancelLabel="Discard"
        message={
          <>
            We found an unsubmitted receipt draft from a previous session.
            Resume it, or discard and start fresh?
          </>
        }
      />

      <BankDetailsUpdateModal
        open={bankPrompt !== null}
        fields={bankPrompt?.fields ?? []}
        onYes={() => {
          const p = bankPrompt;
          setBankPrompt(null);
          if (p !== null) void submitReceipt({ ...p.input, update_customer_bank_details: true });
        }}
        onNo={() => {
          const p = bankPrompt;
          setBankPrompt(null);
          if (p !== null) void submitReceipt({ ...p.input, update_customer_bank_details: false });
        }}
        onBack={() => setBankPrompt(null)}
      />

      {/* Saved drafts browser */}
      <DraftManagerModal
        open={draftsOpen}
        onClose={() => setDraftsOpen(false)}
        draftType="receipt"
        drafts={draftManager.drafts}
        isDirty={draftManager.isDirty}
        isSaving={draftManager.isSaving}
        onRefresh={draftManager.listDrafts}
        onLoad={draftManager.loadDraft}
        onDelete={draftManager.deleteDraft}
        onSaveCurrent={draftManager.saveDraft}
      />
      </div>
      </div>
    </div>
  );
}
